import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Star, Loader2, Filter, SlidersHorizontal } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { products as mockProducts, brands } from '../data/ecommerceMock';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductListingPage = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [priceRange, setPriceRange] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/products?limit=50`);
        if (res.ok) {
          const data = await res.json();
          if (data.length > 0) { setProducts(data); setLoading(false); return; }
        }
      } catch (e) { console.error('API fetch failed:', e); }
      // Fallback to mock data
      setProducts(mockProducts);
      setLoading(false);
    };
    fetchProducts();
  }, []);

  // Filter products
  let filteredProducts = [...products];

  if (selectedBrand !== 'all') {
    filteredProducts = filteredProducts.filter(p => p.brand === selectedBrand);
  }

  if (priceRange !== 'all') {
    const ranges = {
      'under-15k': [0, 15000],
      '15k-20k': [15000, 20000],
      'above-20k': [20000, Infinity]
    };
    const [min, max] = ranges[priceRange];
    filteredProducts = filteredProducts.filter(p => p.price >= min && p.price < max);
  }

  // Sort products
  if (sortBy === 'price-low') {
    filteredProducts.sort((a, b) => a.price - b.price);
  } else if (sortBy === 'price-high') {
    filteredProducts.sort((a, b) => b.price - a.price);
  } else if (sortBy === 'rating') {
    filteredProducts.sort((a, b) => b.rating - a.rating);
  }

  const handleAddToCart = (product, e) => {
    e.stopPropagation();
    addToCart(product);
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.slug || product.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-600 text-white py-8 sm:py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-4">RO Water Purifiers</h1>
          <p className="text-sm sm:text-xl text-blue-100">Premium quality water purifiers from top brands with free installation</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="grid lg:grid-cols-4 gap-4 sm:gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1 hidden lg:block">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-6">
                  <SlidersHorizontal className="text-blue-700" size={24} />
                  <h2 className="text-xl font-bold">Filters</h2>
                </div>

                {/* Brand Filter */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Brand</h3>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="brand"
                        value="all"
                        checked={selectedBrand === 'all'}
                        onChange={(e) => setSelectedBrand(e.target.value)}
                        className="text-blue-700"
                      />
                      <span className="text-gray-700">All Brands</span>
                    </label>
                    {brands.filter(b => b !== 'Universal').map(brand => (
                      <label key={brand} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="brand"
                          value={brand}
                          checked={selectedBrand === brand}
                          onChange={(e) => setSelectedBrand(e.target.value)}
                          className="text-blue-700"
                        />
                        <span className="text-gray-700">{brand}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Price Range Filter */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Price Range</h3>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="price"
                        value="all"
                        checked={priceRange === 'all'}
                        onChange={(e) => setPriceRange(e.target.value)}
                        className="text-blue-700"
                      />
                      <span className="text-gray-700">All Prices</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="price"
                        value="under-15k"
                        checked={priceRange === 'under-15k'}
                        onChange={(e) => setPriceRange(e.target.value)}
                        className="text-blue-700"
                      />
                      <span className="text-gray-700">Under ₹15,000</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="price"
                        value="15k-20k"
                        checked={priceRange === '15k-20k'}
                        onChange={(e) => setPriceRange(e.target.value)}
                        className="text-blue-700"
                      />
                      <span className="text-gray-700">₹15,000 - ₹20,000</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="price"
                        value="above-20k"
                        checked={priceRange === 'above-20k'}
                        onChange={(e) => setPriceRange(e.target.value)}
                        className="text-blue-700"
                      />
                      <span className="text-gray-700">Above ₹20,000</span>
                    </label>
                  </div>
                </div>

                {/* Clear Filters */}
                <Button
                  onClick={() => {
                    setSelectedBrand('all');
                    setPriceRange('all');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Clear All Filters
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            {/* Sort & Count */}
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <p className="text-sm sm:text-base text-gray-700">
                <span className="font-semibold">{filteredProducts.length}</span> products
              </p>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-2 sm:px-4 py-1.5 sm:py-2 border-2 border-gray-200 rounded-lg focus:border-blue-700 focus:outline-none text-sm"
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
              </select>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
            <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6">
              {filteredProducts.map((product) => (
                <Card
                  key={product.id}
                  className="group hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-blue-200 overflow-hidden"
                  onClick={() => handleProductClick(product)}
                >
                  {/* Product Image */}
                  <div className="relative overflow-hidden bg-gray-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-36 sm:h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <Badge className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-lime-500 hover:bg-lime-600 text-white font-semibold text-xs">
                      {product.brand}
                    </Badge>
                    {product.discount > 0 && (
                      <Badge className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-red-500 hover:bg-red-600 text-white font-semibold text-xs">
                        {product.discount}% OFF
                      </Badge>
                    )}
                  </div>

                  <CardContent className="p-3 sm:p-5">
                    {/* Product Info */}
                    <h3 className="text-sm sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2 group-hover:text-blue-700 transition-colors line-clamp-2">
                      {product.name}
                    </h3>

                    {/* Rating */}
                    <div className="flex items-center gap-1 sm:gap-2 mb-2 sm:mb-3">
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={12}
                            className={i < Math.floor(product.rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-gray-600">({product.reviews})</span>
                    </div>

                    {/* Capacity & Tech */}
                    <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4">
                      <span className="font-medium">{product.capacity}</span>
                      <span>•</span>
                      <span>{product.technology}</span>
                    </div>

                    {/* Price */}
                    <div className="flex items-baseline gap-1 sm:gap-2 mb-2 sm:mb-4">
                      <span className="text-base sm:text-2xl font-bold text-blue-700">₹{product.price.toLocaleString()}</span>
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice.toLocaleString()}</span>
                    </div>

                    {/* CTA Button */}
                    <Button
                      onClick={(e) => handleAddToCart(product, e)}
                      className="w-full bg-gradient-to-r from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 text-white font-semibold py-2.5 sm:py-5 rounded-lg shadow-md hover:shadow-lg transition-all text-xs sm:text-base"
                    >
                      <ShoppingCart size={16} className="mr-1 sm:mr-2" />
                      Add to Cart
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            )}

            {/* Empty State */}
            {filteredProducts.length === 0 && (
              <div className="text-center py-16">
                <Filter size={64} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your filters</p>
                <Button
                  onClick={() => {
                    setSelectedBrand('all');
                    setPriceRange('all');
                  }}
                  className="bg-blue-700 hover:bg-blue-800"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductListingPage;
